import React, { FC } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { styled } from 'linaria/react';
import { PaymentButton } from '@shared/ui/atoms';
import { replenishSteamFromNewYearGiveawayClickGtm } from '@shared/lib/gtm';
import { useIntersectionObserver } from '@shared/lib';
import FullViewportHeightSection from './FullViewportHeightSection';
import SectionBg from './SectionBg';
import Title from './Title';
import Description from './Description';
import replenishSteamBgDesktop from '../../assets/new-year/replenish-steam-bg-desktop.jpg';
import replenishSteamBgTablet from '../../assets/new-year/replenish-steam-bg-tablet.jpg';
import replenishSteamBgMobile from '../../assets/new-year/replenish-steam-bg-mobile.jpg';
import snowImg from '../../assets/new-year/snow.png';
import headphonesImg from '../../assets/new-year/headphones.png';
import mtsLogoImg from '../../assets/new-year/mts-logo.png';
import pointerImg from '../../assets/new-year/pointer.png';
import snowflakeImg from '../../assets/new-year/snowflake.png';
import pcImg from '../../assets/new-year/pc.png';
const DynamicScrollDownAnimation = dynamic(() => import('./ScrollDownAnimation').then(imported => imported.default), {
  loading: () => <div />,
  ssr: false
});
const ReplenishSteamSectionView: FC = () => {
  const router = useRouter();
  const [sectionRef, isSectionVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.85,
    triggerOnce: true
  });
  const handleReplenishSteamClick = () => {
    replenishSteamFromNewYearGiveawayClickGtm();
    router.push('/steam');
  };
  return <FullViewportHeightSection>
      <SectionBg desktopImg={replenishSteamBgDesktop} tabletImg={replenishSteamBgTablet} mobileImg={replenishSteamBgMobile}>
        <ReplenishSteamSectionViewWrapper ref={sectionRef}>
          <OpacityAnimation isSectionVisible={isSectionVisible}>
            <TextWrapper>
              <Title>Пополняйте steam в&nbsp;сервисе МТС Оплата</Title>
              <Description>
                Совершайте платежи в период зимней распродажи Steam с&nbsp;18&nbsp;декабря&nbsp;2025&nbsp;г.
                по&nbsp;5&nbsp;января&nbsp;2026&nbsp;г.
              </Description>
            </TextWrapper>
            <ReplenishSteamButtonWrapper>
              <PaymentButton type="button" title="пополнить STEAM" bannerButtonStartIcon="https://staticpayment.ssl.mts.ru/upload/steam-icon.svg" bannerButtonBgColor="linear-gradient(360deg, #E65800 0%, #FFA60C 100%)" bannerButtonBottomColor="#7C341F" onClick={handleReplenishSteamClick} />
            </ReplenishSteamButtonWrapper>
          </OpacityAnimation>
          <SnowWrapper>
            <Image src={snowImg} alt="Снег" width={1920} height={346} />
          </SnowWrapper>
          <HeadphonesWrapper isSectionVisible={isSectionVisible}>
            <Image src={headphonesImg} alt="Наушники" width={302} height={302} />
          </HeadphonesWrapper>
          <MtsLogoWrapper isSectionVisible={isSectionVisible}>
            <Image src={mtsLogoImg} alt="МТС" width={307} height={291} />
          </MtsLogoWrapper>
          <PointerWrapper isSectionVisible={isSectionVisible}>
            <Image src={pointerImg} alt="Стрелка" width={177} height={250} />
          </PointerWrapper>
          <SnowflakeWrapper isSectionVisible={isSectionVisible}>
            <Image src={snowflakeImg} alt="Снежинка" width={310} height={310} />
          </SnowflakeWrapper>
          <PcWrapper isSectionVisible={isSectionVisible}>
            <Image src={pcImg} alt="ПК" width={360} height={360} />
          </PcWrapper>
          <ScrollDownWrapper>
            <DynamicScrollDownAnimation isTextBlack />
          </ScrollDownWrapper>
        </ReplenishSteamSectionViewWrapper>
      </SectionBg>
    </FullViewportHeightSection>;
};
export default ReplenishSteamSectionView;
const ReplenishSteamSectionViewWrapper = /*#__PURE__*/styled('div')({
  name: "ReplenishSteamSectionViewWrapper",
  class: "rjs1gki",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? 'none' : 'translateY(60px)';
const _exp2 = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? '1' : '0';
const OpacityAnimation = /*#__PURE__*/styled('div')({
  name: "OpacityAnimation",
  class: "odj82ac",
  propsAsIs: false,
  vars: {
    "odj82ac-0": [_exp()],
    "odj82ac-1": [_exp2()]
  }
});
const TextWrapper = /*#__PURE__*/styled('div')({
  name: "TextWrapper",
  class: "t126d2oe",
  propsAsIs: false
});
const ReplenishSteamButtonWrapper = /*#__PURE__*/styled('div')({
  name: "ReplenishSteamButtonWrapper",
  class: "rm3u5ar",
  propsAsIs: false
});
const SnowIconWrapper = /*#__PURE__*/styled('div')({
  name: "SnowIconWrapper",
  class: "s1hbcr6k",
  propsAsIs: false
});
const SnowWrapper = /*#__PURE__*/styled('div')({
  name: "SnowWrapper",
  class: "s74tf81",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? 'translateX(-25%)' : 'translate(-100%, -50%)';
const HeadphonesWrapper = /*#__PURE__*/styled('div')({
  name: "HeadphonesWrapper",
  class: "hh2y61u",
  propsAsIs: false,
  vars: {
    "hh2y61u-0": [_exp3()]
  }
});
const _exp4 = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? 'translate(0, 35%)' : 'translate(-20%, 100%)';
const MtsLogoWrapper = /*#__PURE__*/styled('div')({
  name: "MtsLogoWrapper",
  class: "m19h3l5u",
  propsAsIs: false,
  vars: {
    "m19h3l5u-0": [_exp4()]
  }
});
const _exp5 = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? 'translate(0, 0)' : 'translate(20%, 100%)';
const PointerWrapper = /*#__PURE__*/styled('div')({
  name: "PointerWrapper",
  class: "p1akpnpk",
  propsAsIs: false,
  vars: {
    "p1akpnpk-0": [_exp5()]
  }
});
const _exp6 = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? 'translate(0, -35%)' : 'translate(20%, -100%)';
const SnowflakeWrapper = /*#__PURE__*/styled('div')({
  name: "SnowflakeWrapper",
  class: "s7b4b1w",
  propsAsIs: false,
  vars: {
    "s7b4b1w-0": [_exp6()]
  }
});
const _exp7 = /*#__PURE__*/() => ({
  isSectionVisible
}) => isSectionVisible ? 'translate(35%, 0)' : 'translate(100%, -50%)';
const PcWrapper = /*#__PURE__*/styled('div')({
  name: "PcWrapper",
  class: "pidinr0",
  propsAsIs: false,
  vars: {
    "pidinr0-0": [_exp7()]
  }
});
const ScrollDownWrapper = /*#__PURE__*/styled('div')({
  name: "ScrollDownWrapper",
  class: "szbnspd",
  propsAsIs: false
});

require("./ReplenishSteamSectionView.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./ReplenishSteamSectionView.tsx");